import { chmodSync, closeSync, copyFileSync, existsSync, mkdirSync, openSync } from "node:fs";
import { join } from "node:path";
import type { ExecDeps } from "./exec.js";
import type { BootLogger } from "./types.js";

// Persistent authorized_keys copy on the shared emptyDir (mounted in both
// containers, survives workbench restarts for the pod's life). installWorkspaceSshKey
// (apps/api .../workspace-ssh-key.ts) writes it there; we re-hydrate the live
// ~/.ssh/authorized_keys from it on every workbench start so a key installed before
// an OOM restart survives. Kept in sync with SHARED_MOUNT_DEFAULT there.
function persistedAuthorizedKeys(): string {
  return join(
    process.env.CONVEYOR_SHARED_DIR || "/var/run/conveyor-workbench",
    "ssh",
    "authorized_keys",
  );
}

/**
 * Ports `start_workspace_sshd` (entrypoint.sh:12-31). Warn-only end to end —
 * SSH attach is a convenience surface; its absence must never degrade boot.
 *
 * Binds all interfaces deliberately: the preview-router tunnels in from
 * outside the pod to the pod IP. Access is gated by the attach token + SSH
 * public-key auth, not by network reachability (no public ingress).
 */
export async function startWorkspaceSshd(
  exec: ExecDeps["exec"],
  log: BootLogger,
  port: string = process.env.CONVEYOR_WORKSPACE_SSH_PORT || "2222",
  home = "/home/conveyor",
): Promise<void> {
  try {
    const sshDir = join(home, ".ssh");
    mkdirSync(sshDir, { recursive: true });
    chmodSync(sshDir, 0o700);
    const authorizedKeys = join(sshDir, "authorized_keys");
    const persisted = persistedAuthorizedKeys();
    if (existsSync(persisted)) {
      // Restore keys installed before a workbench restart wiped /home/conveyor.
      copyFileSync(persisted, authorizedKeys);
    } else if (!existsSync(authorizedKeys)) {
      closeSync(openSync(authorizedKeys, "a"));
    }
    chmodSync(authorizedKeys, 0o600);
  } catch (err) {
    log.warn(
      `[boot] WARN: workspace SSHD key setup failed: ${err instanceof Error ? err.message : String(err)}`,
    );
    return;
  }
  if (!existsSync("/usr/sbin/sshd")) {
    log.warn("[boot] WARN: workspace SSHD unavailable");
    return;
  }
  try {
    await exec("sudo", ["mkdir", "-p", "/run/sshd"], { timeoutMs: 10_000 });
    await exec("sudo", ["/usr/sbin/sshd", "-o", "ListenAddress=0.0.0.0", "-o", `Port=${port}`], {
      timeoutMs: 10_000,
    });
    log.info(`[boot] Workspace SSHD listening on 0.0.0.0:${port}`);
  } catch {
    log.warn("[boot] WARN: workspace SSHD failed to start");
  }
}
